import { useEffect, useState } from "react";
import { useAlert } from "../../functions/useAlert";
import {
  fetchOrgStorageStats,
  fetchSharePointDrives as fetchDrives,
  updateSharePointDriveLocation,
  startMigration,
  pauseMigration,
  resumeMigration,
} from "../../services/connectors.service";
import { updateStorageProvider } from "../../services/organisations.service";

interface UseStorageProvidersOptions {
  selectedOrganization: string;
  selectedOrgObj: any;
  fetchOrganisations: () => Promise<void>;
}

export function useStorageProviders({
  selectedOrganization,
  selectedOrgObj,
  fetchOrganisations,
}: UseStorageProvidersOptions) {
  const [setAlert] = useAlert();

  const [storageProvider, setStorageProvider] = useState<string>("AZURE");
  const [isSwitchingStorage, setIsSwitchingStorage] = useState<boolean>(false);
  const [storageConfirmDialog, setStorageConfirmDialog] =
    useState<boolean>(false);
  const [pendingStorageProvider, setPendingStorageProvider] =
    useState<string>("");
  const [storageStats, setStorageStats] = useState<any>(null);
  const [isMigrationActionLoading, setIsMigrationActionLoading] =
    useState(false);
  const [availableDrives, setAvailableDrives] = useState<any[]>([]);
  const [isFetchingDrives, setIsFetchingDrives] = useState<boolean>(false);
  const [selectedDriveId, setSelectedDriveId] = useState<string>("");
  const [isSiteChangeDialogOpen, setIsSiteChangeDialogOpen] =
    useState<boolean>(false);
  const [pendingDriveSelection, setPendingDriveSelection] =
    useState<string>("");

  const fetchStorageStats = async () => {
    if (!selectedOrganization || selectedOrganization === "All Organisation")
      return;
    try {
      const res: any = await fetchOrgStorageStats(selectedOrganization);
      setStorageStats(res);

      if (res.currentProvider) {
        setStorageProvider((prevProvider) => {
          if (res.currentProvider !== prevProvider) {
            fetchOrganisations();
            return res.currentProvider;
          }
          return prevProvider;
        });
      }
    } catch (err) {
      console.error("Failed to fetch storage stats", err);
    }
  };

  const fetchSharePointDrives = async () => {
    if (!selectedOrganization) return;
    setIsFetchingDrives(true);
    try {
      const response: any = await fetchDrives(selectedOrganization);
      if (response?.drives) {
        setAvailableDrives(response.drives);
      }
    } catch (error) {
      console.error("Failed to fetch drives:", error);
      setAlert("Failed to load Microsoft Teams locations", "error");
    } finally {
      setIsFetchingDrives(false);
    }
  };

  const openSiteChangeDialog = () => {
    setPendingDriveSelection(
      selectedOrgObj?.storageSettings?.sharepointDriveId || "",
    );
    setIsSiteChangeDialogOpen(true);
    fetchSharePointDrives();
  };

  const handleConfirmSiteChange = async () => {
    if (!pendingDriveSelection) {
      setAlert("Please select a target site.", "warning");
      return;
    }
    const selectedDrive = availableDrives.find(
      (d) => d.driveId === pendingDriveSelection,
    );
    if (!selectedDrive) return;

    setIsMigrationActionLoading(true);
    try {
      await updateSharePointDriveLocation(selectedOrganization, {
        sharepointDriveId: selectedDrive.driveId,
        sharepointSiteName: selectedDrive.siteName,
        sharepointWebUrl: selectedDrive.webUrl,
      });
      setSelectedDriveId(pendingDriveSelection);
      setAlert(
        `Direct migration initiated. Files are moving to ${selectedDrive.siteName}`,
        "success",
      );
      setIsSiteChangeDialogOpen(false);
      fetchOrganisations();
      fetchStorageStats();
      setTimeout(fetchStorageStats, 1000);
      setTimeout(fetchStorageStats, 3000);
    } catch (error) {
      setAlert("Failed to update storage location", "error");
    } finally {
      setIsMigrationActionLoading(false);
    }
  };

  const handleMigrationAction = async (
    action: "start" | "pause" | "resume",
    targetProvider?: string,
  ) => {
    setIsMigrationActionLoading(true);
    try {
      if (action === "start") {
        await startMigration(selectedOrganization, targetProvider!);
        setStorageConfirmDialog(false);
        setPendingStorageProvider("");
        setAlert(
          `Migration to ${targetProvider === "SHAREPOINT" ? "SharePoint" : "Prospo Storage"} started.`,
          "success",
        );
      } else if (action === "pause") {
        await pauseMigration(selectedOrganization);
        setAlert(
          "Migration paused. It will stop safely after the current file.",
          "info",
        );
      } else if (action === "resume") {
        await resumeMigration(selectedOrganization);
        setAlert("Migration resumed.", "success");
      }

      fetchStorageStats();
      setTimeout(fetchStorageStats, 1000);
      setTimeout(fetchStorageStats, 3000);
    } catch (err: any) {
      setAlert(err.message || `Failed to ${action} migration`, "error");
    } finally {
      setIsMigrationActionLoading(false);
    }
  };

  const handleToggleStorageProvider = async () => {
    if (!selectedOrganization || selectedOrganization === "All Organisation") {
      setAlert("Please select an organisation first", "warning");
      return;
    }
    const targetProvider = pendingStorageProvider;
    setIsSwitchingStorage(true);
    setStorageConfirmDialog(false);
    try {
      const response: any = await updateStorageProvider(
        selectedOrganization,
        targetProvider,
      );
      setStorageProvider(targetProvider);
      setAlert(
        response.message || "Storage provider updated successfully!",
        "success",
      );
      fetchOrganisations();
    } catch (error: any) {
      setAlert(error.message || "Failed to update storage provider", "error");
    } finally {
      setIsSwitchingStorage(false);
      setPendingStorageProvider("");
    }
  };

  const handleStorageProviderClick = () => {
    const targetProvider = storageProvider === "AZURE" ? "SHAREPOINT" : "AZURE";
    setPendingStorageProvider(targetProvider);
    if (targetProvider === "SHAREPOINT") {
      fetchSharePointDrives();
      setSelectedDriveId(
        selectedOrgObj?.storageSettings?.sharepointDriveId || "",
      );
    }
    setStorageConfirmDialog(true);
  };

  const confirmAndStartMigration = async () => {
    if (pendingStorageProvider === "SHAREPOINT") {
      if (!selectedDriveId) {
        setAlert(
          "Please select a target SharePoint site before migrating.",
          "warning",
        );
        return;
      }
      if (
        selectedDriveId !== selectedOrgObj?.storageSettings?.sharepointDriveId
      ) {
        setIsMigrationActionLoading(true);
        const selectedDrive = availableDrives.find(
          (d) => d.driveId === selectedDriveId,
        );
        if (selectedDrive) {
          try {
            await updateSharePointDriveLocation(selectedOrganization, {
              sharepointDriveId: selectedDrive.driveId,
              sharepointSiteName: selectedDrive.siteName,
              sharepointWebUrl: selectedDrive.webUrl,
            });
            // Re-fetch org list so selectedOrgObj reflects the new drive settings
            await fetchOrganisations();
          } catch (e) {
            setAlert(
              "Failed to update target location. Check permissions.",
              "error",
            );
            setIsMigrationActionLoading(false);
            return;
          }
        }
        setIsMigrationActionLoading(false);
      }
    }
    handleMigrationAction("start", pendingStorageProvider);
  };

  // Poll for migration status while in progress
  useEffect(() => {
    let interval: any;
    if (storageStats?.migrationState?.status === "IN_PROGRESS") {
      interval = setInterval(() => {
        fetchStorageStats();
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [storageStats?.migrationState?.status]);

  return {
    storageProvider,
    setStorageProvider,
    isSwitchingStorage,
    storageConfirmDialog,
    setStorageConfirmDialog,
    pendingStorageProvider,
    setPendingStorageProvider,
    storageStats,
    isMigrationActionLoading,
    availableDrives,
    isFetchingDrives,
    selectedDriveId,
    setSelectedDriveId,
    isSiteChangeDialogOpen,
    setIsSiteChangeDialogOpen,
    pendingDriveSelection,
    setPendingDriveSelection,
    fetchStorageStats,
    fetchSharePointDrives,
    openSiteChangeDialog,
    handleConfirmSiteChange,
    handleMigrationAction,
    handleToggleStorageProvider,
    handleStorageProviderClick,
    confirmAndStartMigration,
  };
}
